import React, { useEffect, useState, useRef } from "react";
import { Leaf, TrendingUp } from "lucide-react";
import { MyForest } from "./MyForest";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * EarthImpactCard — the live-ticking "your pack's environmental credit"
 * card on the mobile app dashboard.
 *
 * Fetches per-VIN + platform aggregate impact once, then animates the
 * numbers ticking upward locally using the backend-computed
 * `rate_per_hour` values. Feels alive without hammering the API — one
 * refresh per mount is enough because the drift is deterministic.
 *
 * When we don't have a passport for the VIN yet, the card renders in a
 * quieter "generate a passport to unlock this" state so it never
 * confuses new owners.
 */
export const EarthImpactCard = ({ vin }) => {
  const [impact, setImpact] = useState(null);   // per-VIN
  const [platform, setPlatform] = useState(null); // aggregate
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const anchorTs = useRef(Date.now());

  useEffect(() => {
    if (!vin || !API_BASE) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [pv, agg] = await Promise.all([
          fetch(`${API_BASE}/api/earth/impact/${encodeURIComponent(vin)}`).then((r) => (r.ok ? r.json() : null)),
          fetch(`${API_BASE}/api/earth/impact/aggregate`).then((r) => (r.ok ? r.json() : null)),
        ]);
        if (!cancelled) {
          setImpact(pv);
          setPlatform(agg);
          anchorTs.current = Date.now();
        }
      } catch (_e) {
        if (!cancelled) setError(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [vin]);

  // Re-render every 800ms so the counter appears to tick continuously.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 800);
    return () => clearInterval(id);
  }, []);

  if (error) return null;

  // Not-yet state — visible but non-blocking; guides toward a passport.
  if (impact && !impact.exists) {
    return (
      <div
        className="rounded-2xl border p-4"
        style={{ borderColor: "rgba(57,255,20,0.28)", background: "rgba(57,255,20,0.05)" }}
        data-testid="earth-impact-card-empty"
      >
        <div className="flex items-center gap-2 mb-1">
          <Leaf className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.4} />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#39FF14" }}>
            EVIQ Earth Impact
          </div>
        </div>
        <div className="text-[13px] text-zinc-200 leading-snug">
          Generate a Battery Passport to unlock your personal CO₂ deferred, cells kept in service, and freshwater preserved.
        </div>
        <MyForest vin={vin} />
      </div>
    );
  }

  if (!impact) {
    return (
      <div
        className="rounded-2xl border p-4"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
        data-testid="earth-impact-card-loading"
      >
        <div className="h-3 w-40 rounded bg-white/[0.06] animate-pulse mb-3" />
        <div className="h-8 w-32 rounded bg-white/[0.06] animate-pulse" />
      </div>
    );
  }

  // Ticker math — extrapolate from the fetched baseline using elapsed
  // wall-clock hours × per-hour rate. This is stable across re-renders.
  const elapsedH = (Date.now() - anchorTs.current) / 3.6e6;
  const r = impact.rate_per_hour || {};
  const co2Live = impact.co2_deferred_kg + (r.co2_kg_per_hour || 0) * elapsedH;
  const waterLive = impact.water_preserved_l + (r.water_l_per_hour || 0) * elapsedH;
  const cellsLive = impact.cells_kept_in_service + (r.cells_per_hour || 0) * elapsedH;

  const platformR = platform?.rate_per_hour || {};
  const platformCo2Live = platform
    ? platform.co2_deferred_kg + (platformR.co2_kg_per_hour || 0) * elapsedH
    : null;

  return (
    <div
      className="rounded-2xl border p-4 relative overflow-hidden"
      style={{
        borderColor: "rgba(57,255,20,0.32)",
        background: "linear-gradient(135deg, rgba(57,255,20,0.08) 0%, rgba(0,229,255,0.04) 100%)",
      }}
      data-testid="earth-impact-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "rgba(57,255,20,0.16)", border: "1px solid rgba(57,255,20,0.45)" }}
          >
            <Leaf className="w-3.5 h-3.5" style={{ color: "#39FF14" }} strokeWidth={2.6} />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#39FF14" }}>
              EVIQ Earth Impact · live
            </div>
            <div className="text-[12px] text-zinc-300 leading-tight truncate">
              Your pack has kept these numbers going down.
            </div>
          </div>
        </div>
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse"
          style={{ background: "#39FF14", boxShadow: "0 0 6px #39FF14" }}
          aria-label="Live counter"
        />
      </div>

      {/* Big number: CO₂ deferred */}
      <div className="mb-4">
        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
          CO₂ deferred
        </div>
        <div className="flex items-baseline gap-2">
          <div
            className="font-display text-4xl font-semibold leading-none tabular-nums"
            style={{ color: "#39FF14" }}
            data-testid="earth-impact-co2"
          >
            {co2Live.toLocaleString(undefined, { maximumFractionDigits: 1 })}
          </div>
          <div className="font-mono text-[10px] text-zinc-500">kg CO₂e</div>
        </div>
        <div className="font-mono text-[10px] text-zinc-500 mt-1">
          ≈ {impact.gasoline_miles_avoided.toLocaleString()} miles of gasoline driving avoided
        </div>
      </div>

      {/* Sub-stats: cells + water */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div
          className="rounded-lg border p-2.5"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.28)" }}
        >
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">
            Cells kept in service
          </div>
          <div
            className="font-display text-lg font-semibold tabular-nums"
            style={{ color: "#00E5FF" }}
            data-testid="earth-impact-cells"
          >
            {Math.round(cellsLive).toLocaleString()}
          </div>
          <div className="font-mono text-[9px] text-zinc-500">
            of {impact.cells_total.toLocaleString()} in pack
          </div>
        </div>
        <div
          className="rounded-lg border p-2.5"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.28)" }}
        >
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">
            Freshwater preserved
          </div>
          <div
            className="font-display text-lg font-semibold tabular-nums"
            style={{ color: "#7FD1FF" }}
            data-testid="earth-impact-water"
          >
            {Math.round(waterLive).toLocaleString()}
          </div>
          <div className="font-mono text-[9px] text-zinc-500">litres · Li mining</div>
        </div>
      </div>

      {/* Platform total — social proof */}
      {platformCo2Live != null && (
        <div
          className="flex items-center gap-2 rounded-lg border px-3 py-2"
          style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.24)" }}
        >
          <TrendingUp className="w-3.5 h-3.5 text-zinc-400" strokeWidth={2.4} />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
              Platform total · {platform.vins_tracked} pack{platform.vins_tracked === 1 ? "" : "s"}
            </div>
            <div className="text-[12px] text-white leading-tight">
              <span
                className="font-display font-semibold tabular-nums"
                style={{ color: "#39FF14" }}
                data-testid="earth-impact-platform-co2"
              >
                {platformCo2Live.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>{" "}
              <span className="text-zinc-400">kg CO₂e deferred</span>
            </div>
          </div>
        </div>
      )}

      {/* My Forest — subscription-tied bonsai pledge ledger */}
      <MyForest vin={vin} />

      {/* Method disclosure */}
      <div className="mt-3 font-mono text-[9px] text-zinc-600 leading-relaxed">
        Argonne GREET · {impact.months_in_service.toFixed(0)} mo in service · updated live
      </div>

      {/* Hidden re-render tick to force the counter forward. */}
      <span className="sr-only" aria-hidden="true">{tick}</span>
    </div>
  );
};
